import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type {
  CodingSessionSummary,
  CodingMessage,
  CodingSessionStatus,
  CodingPermissionRequest,
  CodingConfig,
} from '../../coding/types';

interface CodingState {
  /** 当前选中的 desktop 设备（relay targetClientId）。null 表示未选/无在线设备。 */
  selectedClientId: string | null;
  /** 当前设备上的会话摘要列表（来自 coding.session.list / created / deleted）。 */
  sessions: CodingSessionSummary[];
  /** 当前打开的会话。 */
  currentSessionId: string | null;
  /** 按会话隔离的消息（扁平结构，按 id upsert）。 */
  messagesBySession: Record<string, CodingMessage[]>;
  /** 按会话的运行状态。 */
  statusBySession: Record<string, CodingSessionStatus>;
  /** 按会话的待处理权限请求（一次一个）。 */
  pendingPermissionBySession: Record<string, CodingPermissionRequest | null>;
  /** 按会话的排队消息数。 */
  queuedCountBySession: Record<string, number>;
  /** 当前会话是否正在流式输出。 */
  isStreaming: boolean;
  /** desktop config.get 返回的模型/权限模式/默认目录。 */
  config: CodingConfig | null;
}

const initialState: CodingState = {
  selectedClientId: null,
  sessions: [],
  currentSessionId: null,
  messagesBySession: {},
  statusBySession: {},
  pendingPermissionBySession: {},
  queuedCountBySession: {},
  isStreaming: false,
  config: null,
};

const codingSlice = createSlice({
  name: 'coding',
  initialState,
  reducers: {
    setSelectedClient(state, action: PayloadAction<string | null>) {
      if (state.selectedClientId === action.payload) return;
      // 切换设备 → 清空与上一台设备绑定的会话视图（会话数据按设备隔离）。
      state.selectedClientId = action.payload;
      state.sessions = [];
      state.currentSessionId = null;
      state.messagesBySession = {};
      state.statusBySession = {};
      state.pendingPermissionBySession = {};
      state.queuedCountBySession = {};
      state.isStreaming = false;
    },

    setSessions(state, action: PayloadAction<CodingSessionSummary[]>) {
      state.sessions = action.payload;
    },

    upsertSession(state, action: PayloadAction<CodingSessionSummary>) {
      const summary = action.payload;
      const idx = state.sessions.findIndex((s) => s.id === summary.id);
      if (idx !== -1) state.sessions[idx] = { ...state.sessions[idx], ...summary };
      else state.sessions.unshift(summary);
    },

    /** coding.session.updated：仅带部分字段（status/model/permissionMode/updatedAt）。 */
    patchSession(state, action: PayloadAction<{ sessionId: string } & Partial<CodingSessionSummary>>) {
      const { sessionId, ...patch } = action.payload;
      const idx = state.sessions.findIndex((s) => s.id === sessionId);
      if (idx !== -1) state.sessions[idx] = { ...state.sessions[idx], ...patch };
    },

    removeSession(state, action: PayloadAction<string>) {
      const id = action.payload;
      state.sessions = state.sessions.filter((s) => s.id !== id);
      delete state.messagesBySession[id];
      delete state.statusBySession[id];
      delete state.pendingPermissionBySession[id];
      delete state.queuedCountBySession[id];
      if (state.currentSessionId === id) state.currentSessionId = null;
    },

    setCurrentSessionId(state, action: PayloadAction<string | null>) {
      state.currentSessionId = action.payload;
    },

    setMessages(state, action: PayloadAction<{ sessionId: string; messages: CodingMessage[] }>) {
      state.messagesBySession[action.payload.sessionId] = action.payload.messages;
    },

    /** coding.stream.message：按 id upsert，保持插入顺序。 */
    upsertMessage(state, action: PayloadAction<{ sessionId: string; message: CodingMessage }>) {
      const { sessionId, message } = action.payload;
      const list = state.messagesBySession[sessionId] ?? (state.messagesBySession[sessionId] = []);
      const idx = list.findIndex((m) => m.id === message.id);
      if (idx !== -1) list[idx] = { ...list[idx], ...message };
      else list.push(message);
    },

    /** coding.stream.messageUpdate：content 为累积全文，覆盖。 */
    updateMessageContent(state, action: PayloadAction<{ sessionId: string; messageId: string; content: string }>) {
      const { sessionId, messageId, content } = action.payload;
      const list = state.messagesBySession[sessionId];
      if (!list) return;
      const msg = list.find((m) => m.id === messageId);
      if (msg) msg.content = content;
    },

    setStatus(state, action: PayloadAction<{ sessionId: string; status: CodingSessionStatus }>) {
      const { sessionId, status } = action.payload;
      state.statusBySession[sessionId] = status;
      const idx = state.sessions.findIndex((s) => s.id === sessionId);
      if (idx !== -1) state.sessions[idx].status = status;
    },

    enqueuePermission(state, action: PayloadAction<CodingPermissionRequest>) {
      state.pendingPermissionBySession[action.payload.sessionId] = action.payload;
    },

    /** 清除待处理权限（requestId 不匹配时不清，避免误清新请求）。 */
    clearPermission(state, action: PayloadAction<{ sessionId: string; requestId?: string }>) {
      const { sessionId, requestId } = action.payload;
      const cur = state.pendingPermissionBySession[sessionId];
      if (!cur) return;
      if (requestId && cur.requestId !== requestId) return;
      state.pendingPermissionBySession[sessionId] = null;
    },

    setQueuedCount(state, action: PayloadAction<{ sessionId: string; count: number }>) {
      state.queuedCountBySession[action.payload.sessionId] = action.payload.count;
    },

    setStreaming(state, action: PayloadAction<boolean>) {
      state.isStreaming = action.payload;
    },

    setConfig(state, action: PayloadAction<CodingConfig>) {
      state.config = action.payload;
    },

    resetCoding() {
      return initialState;
    },
  },
});

export const {
  setSelectedClient,
  setSessions,
  upsertSession,
  patchSession,
  removeSession,
  setCurrentSessionId,
  setMessages,
  upsertMessage,
  updateMessageContent,
  setStatus,
  enqueuePermission,
  clearPermission,
  setQueuedCount,
  setStreaming,
  setConfig,
  resetCoding,
} = codingSlice.actions;

export default codingSlice.reducer;
